import { motion } from 'framer-motion';
import { Shield, Lock, Eye } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const copy: Record<string, { compact: string; title: string; points: string[] }> = {
  en: {
    compact: 'You decide when and how to share',
    title: "You're in control of your words.",
    points: [
      'Your words are private unless you choose to share them',
      'Your Words. Your Timing. Your Decision.',
      'What you create belongs to you',
    ],
  },
  es: {
    compact: 'Tú decides cuándo y cómo compartir',
    title: 'Tú controlas tus palabras.',
    points: [
      'Tus palabras son privadas a menos que elijas compartirlas',
      'Tus Palabras. Tu Momento. Tu Decisión.',
      'Lo que creas te pertenece',
    ],
  },
  fr: {
    compact: 'Vous décidez quand et comment partager',
    title: 'Vous contrôlez vos mots.',
    points: [
      'Vos mots restent privés sauf si vous choisissez de les partager',
      'Vos Mots. Votre Moment. Votre Décision.',
      'Ce que vous créez vous appartient',
    ],
  },
  pt: {
    compact: 'Você decide quando e como compartilhar',
    title: 'Você controla suas palavras.',
    points: [
      'Suas palavras são privadas a menos que você escolha compartilhá-las',
      'Suas Palavras. Seu Momento. Sua Decisão.',
      'O que você cria pertence a você',
    ],
  },
  ar: {
    compact: 'أنت تقرر متى وكيف تشارك',
    title: 'أنت تتحكم في كلماتك.',
    points: [
      'كلماتك خاصة ما لم تختر مشاركتها',
      'كلماتك. توقيتك. قرارك.',
      'ما تنشئه ملك لك',
    ],
  },
  ja: {
    compact: 'いつ、どのように共有するかはあなたが決めます',
    title: 'あなたの言葉はあなたのもの。',
    points: [
      'あなたの言葉は共有を選ばない限りプライベートです',
      'あなたの言葉。あなたのタイミング。あなたの決断。',
      'あなたが作ったものはあなたのものです',
    ],
  },
  ko: {
    compact: '언제 어떻게 공유할지 당신이 결정합니다',
    title: '당신의 말은 당신이 관리합니다.',
    points: [
      '공유를 선택하지 않는 한 당신의 말은 비공개입니다',
      '당신의 말. 당신의 타이밍. 당신의 결정.',
      '당신이 만든 것은 당신의 것입니다',
    ],
  },
  id: {
    compact: 'Anda yang memutuskan kapan dan bagaimana berbagi',
    title: 'Anda mengendalikan kata-kata Anda.',
    points: [
      'Kata-kata Anda bersifat pribadi kecuali Anda memilih untuk membagikannya',
      'Kata-kata Anda. Waktu Anda. Keputusan Anda.',
      'Apa yang Anda buat adalah milik Anda',
    ],
  },
};

interface PrivacyReassuranceProps {
  variant?: 'full' | 'compact';
  className?: string;
}

export function PrivacyReassurance({
  variant = 'full',
  className = ''
}: PrivacyReassuranceProps) {
  const { language } = useLanguage();
  const t = copy[language] || copy.en;
  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-2 text-sm text-muted-foreground ${className}`}
      >
        <Shield className="h-4 w-4 text-primary/70" />
        <span>{t.compact}</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`bg-card rounded-2xl p-6 md:p-8 border border-primary/20 shadow-sm ${className}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-full bg-primary/10">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
          {t.title}
        </h3>
      </div>

      <ul className="space-y-3">
        <li className="flex items-start gap-3">
          <Lock className="h-4 w-4 text-primary/70 mt-0.5" />
          <p className="text-foreground/80 text-sm">
            {t.points[0]}
          </p>
        </li>
        <li className="flex items-start gap-3">
          <Eye className="h-4 w-4 text-primary/70 mt-0.5" />
          <p className="text-foreground/80 text-sm">
            {t.points[1]}
          </p>
        </li>
        <li className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-primary/70 mt-0.5" />
          <p className="text-foreground/80 text-sm">
            {t.points[2]}
          </p>
        </li>
      </ul>
    </motion.div>
  );
}